import { ArrowLeft, Folder, FolderOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { StudentCourseCard } from '../components/StudentCourseCard';
import { EmptyState, ErrorState, ImageWithFallback, LoadingState } from '../components/ui';
import { courseFolderIcon } from '../lib/courseFolders';
import { loadStudentCourseFolderContents } from '../lib/data';
import type { CourseFolder, StudentCourse } from '../lib/types';

export function StudentCourseFolder() {
  const { folderId = '' } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState<CourseFolder | null>(null);
  const [courses, setCourses] = useState<StudentCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    loadStudentCourseFolderContents(folderId).then(({ folder: selected, courses: assignedCourses }) => {
      if (!active) return;
      setFolder(selected);
      setCourses(assignedCourses);
    }).catch(() => { if (active) setLoadFailed(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [folderId]);

  if (loading) return <div className="course-folder-page"><LoadingState /></div>;
  if (loadFailed) return <div className="course-folder-page"><ErrorState onRetry={() => window.location.reload()} /></div>;
  if (!folder) return <div className="course-folder-page">
    <Link to="/courses" className="back-link"><ArrowLeft size={17} /> My courses</Link>
    <EmptyState icon={FolderOpen} title="Folder not available" description="This course folder may have been removed. Return to My courses to browse the available folders." action={<Link className="btn btn-soft" to="/courses">Browse folders</Link>} />
  </div>;

  const Icon = courseFolderIcon(folder.Icon);
  return <div className="course-folder-page">
    <Link to="/courses" className="back-link"><ArrowLeft size={17} /> My courses</Link>
    <header className="course-folder-page-heading">
      <span className="course-folder-page-art"><ImageWithFallback src={folder.Image} alt="" className="course-folder-page-image" fallback={<Icon size={27} aria-hidden="true" />} /></span>
      <div><h1>{folder.FolderName || 'Course folder'}</h1><p>{courses.length} {courses.length === 1 ? 'course' : 'courses'}</p></div>
    </header>
    {courses.length ? <section aria-label={`Courses in ${folder.FolderName || 'this folder'}`}><div className="course-list">{courses.map((course, index) => <StudentCourseCard key={course.objectId} course={course} index={index} catalogMode onOpen={() => !course.objectId.startsWith('fallback') && navigate(`/courses/${course.objectId}`)} />)}</div></section> : <EmptyState icon={Folder} title="No courses in this folder yet" description="Courses assigned to this folder will appear here." action={<Link className="btn btn-soft" to="/courses">Back to courses</Link>} />}
  </div>;
}
